import React, { useState } from 'react';
import { CustomFormValidators, FormValidationResult, ValidationResult } from '@features/common/helpers/validators';
import { ObjectHelpers } from '@features/common/helpers/object.helpers';

interface Form<T> {
    value: T,
    errors: FormValidationResult,
    hasErrors: boolean,
    validateAll: () => boolean,
    handleFormChange: (event: React.ChangeEvent<HTMLInputElement> | {
        target: { name: string, value: any, checked?: any, type?: string }
    }) => void,
    isValid: () => T | null,
    setState: (newState: T) => void // changes state without firing change event
}

export abstract class FormBase<T> {
    abstract validators: CustomFormValidators<T>;
}

export default function useForm<T extends FormBase<T>>(initialState: T, changeTracker?: (hasChanges: boolean) => void): Form<T> {
    const [formState, setFormState] = useState<T>(initialState);
    const [errorState, setErrorState] = useState<FormValidationResult>({});

    const validate: (
        field: keyof T, value: string | any,
        currentErrorState: FormValidationResult,
    ) => FormValidationResult = (field, value, currentErrorState) => {
        const fieldValidators = formState.validators[field];
        let result: ValidationResult = {};
        if (fieldValidators) {
            fieldValidators.forEach((v) => {
                const single = v(value, formState);
                if (single) {
                    result = { ...result, ...single };
                }
            });
        }

        const errorSummary: ValidationResult = { errorSummary: Object.values(result || {}).join(', ') };
        const fieldErrors = { [field]: Object.keys(result).length > 0 ? { ...result, ...errorSummary } : null };
        const nextErrors = ObjectHelpers.removeNullAndUndefined({ ...currentErrorState, ...fieldErrors });

        return nextErrors;
    };

    const hasErrors: boolean = Object.keys(errorState).length > 0;

    const validateAll: () => boolean = () => {
        let newErrorState: FormValidationResult = {};
        Object.keys(formState).forEach((field) => {
            const typedField = field as keyof T;
            const value = formState[typedField];
            newErrorState = validate(typedField, value, newErrorState);
        });

        const errors = Object.keys(newErrorState).length > 0;
        setErrorState(newErrorState);
        return !errors;
    };

    const isValid: () => T | null = () => (validateAll() ? formState : null);

    const setState = (newState: T) => {
        setFormState({ ...formState, ...newState });
    };

    const handleFormChange: (event: React.ChangeEvent<HTMLInputElement> | {
        target: { name: string, value: any, checked?: any, type?: string }
    }) => void = (event) => {
        const field = event.target.name as keyof T;
        switch (event.target.type) {
            case 'checkbox': {
                const value = event.target.checked;
                if (Array.isArray(formState[field])) {
                    const checkedValue = event.target.value;
                    const arr = formState[field] as unknown as any[];
                    const newValue = arr.filter((v) => v !== checkedValue);
                    if (value) {
                        newValue.push(checkedValue);
                    }
                    formState[field] = [...newValue] as unknown as T[keyof T];
                } else {
                    formState[field] = value;
                }

                break;
            }
            default:
                formState[field] = event.target.value;
                break;
        }
        setFormState(formState);

        const newErrorState = validate(field, formState[field], errorState);
        setErrorState(newErrorState);

        if (changeTracker) {
            changeTracker(true);
        }
    };

    return {
        value: formState,
        errors: errorState,
        hasErrors,
        validateAll,
        handleFormChange,
        isValid,
        setState,
    };
}
